import { useEffect, useState } from "react";
import { X, LogOut, LayoutGrid, MessageSquare, Plus, Loader2, Lock, Film, Trash2, Clapperboard } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";
import AdminShowForm from "@/components/AdminShowForm";
import OtpResetForm from "@/components/OtpResetForm";

const TOKEN_KEY = "viewna_admin_token";
const INPUT_CLS =
  "w-full rounded-xl border border-stone-700/70 bg-ink-900/80 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-500 transition-colors focus:border-gold-500/60 focus:outline-none focus:ring-1 focus:ring-gold-500/40";

function ShowsTable({ onEdit, onChanged }) {
  const [shows, setShows] = useState(null);

  const load = () =>
    api.get("/shows").then((r) => setShows(r.data)).catch(() => toast.error("Could not load shows"));

  useEffect(() => { load(); }, []);

  const remove = async (show) => {
    if (!window.confirm(`Delete "${show.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/shows/${show.id}`);
      toast.success("Show deleted");
      onChanged();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!shows) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gold-500" /></div>;
  }

  return (
    <div data-testid="admin-shows-table" className="overflow-x-auto rounded-xl border border-stone-800">
      <table className="w-full min-w-[760px] text-left text-sm">
        <thead className="bg-ink-900/80 text-[10px] uppercase tracking-[0.2em] text-stone-500">
          <tr>
            <th className="px-4 py-3">Show</th>
            <th className="px-4 py-3">Category</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Access</th>
            <th className="px-4 py-3">Badges</th>
            <th className="px-4 py-3">Trailer</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-800/80">
          {shows.map((s) => (
            <tr key={s.id || s.slug} className="hover:bg-ink-900/50">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  {s.poster_url ? (
                    <img src={fileUrl(s.poster_url)} alt={s.title} className="h-14 w-10 rounded object-cover" />
                  ) : (
                    <div className="flex h-14 w-10 items-center justify-center rounded bg-ink-800"><Film className="h-4 w-4 text-stone-600" /></div>
                  )}
                  <div>
                    <p className="font-semibold text-stone-100">{s.title}</p>
                    <p className="text-xs text-stone-500">{s.is_launch ? "Launch show · " : ""}Trend #{s.trending_rank}</p>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 text-stone-400">{s.category}</td>
              <td className="px-4 py-3">
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${s.status === "live" ? "bg-gold-500/15 text-gold-300 border border-gold-500/40" : "border border-stone-600/60 text-stone-400"}`}>
                  {s.status}
                </span>
              </td>
              <td className="px-4 py-3">
                <span data-testid={`admin-show-access-${s.slug}`} className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${s.access === "premium" ? "bg-gradient-to-r from-gold-600 to-gold-400 text-ink-950" : "border border-stone-600/60 text-stone-400"}`}>
                  {s.access === "premium" ? "Premium" : "Free"}
                </span>
              </td>
              <td className="px-4 py-3 text-xs text-gold-300">{(s.badges || []).join(", ") || "—"}</td>
              <td className="px-4 py-3 text-xs text-stone-500">{s.trailer_url ? "Set" : "—"}</td>
              <td className="px-4 py-3 text-right">
                <button data-testid={`admin-edit-show-${s.slug}`} onClick={() => onEdit(s)} className="rounded-lg border border-gold-500/40 px-3 py-1.5 text-xs text-gold-300 transition-colors hover:bg-gold-500/10">Edit</button>
                <button data-testid={`admin-delete-show-${s.slug}`} onClick={() => remove(s)} className="ml-2 rounded-lg border border-red-500/40 px-3 py-1.5 text-xs text-red-400 transition-colors hover:bg-red-500/10">Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QueriesTable() {
  const [messages, setMessages] = useState(null);

  const load = () =>
    api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => toast.error("Could not load queries"));

  useEffect(() => { load(); }, []);

  const remove = async (m) => {
    if (!window.confirm(`Delete query from "${m.name}"?`)) return;
    try {
      await api.delete(`/admin/messages/${m.id}`);
      toast.success("Query deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!messages) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gold-500" /></div>;
  }

  return (
    <div data-testid="admin-queries-table" className="overflow-x-auto rounded-xl border border-stone-800">
      <table className="w-full min-w-[760px] text-left text-sm">
        <thead className="bg-ink-900/80 text-[10px] uppercase tracking-[0.2em] text-stone-500">
          <tr>
            <th className="px-4 py-3">From</th>
            <th className="px-4 py-3">Phone</th>
            <th className="px-4 py-3">Subject</th>
            <th className="px-4 py-3">Message</th>
            <th className="px-4 py-3">Received</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-800/80">
          {messages.length === 0 ? (
            <tr><td colSpan={6} className="px-4 py-10 text-center text-stone-500">No contact queries yet.</td></tr>
          ) : (
            messages.map((m) => (
              <tr key={m.id} className="align-top hover:bg-ink-900/50">
                <td className="px-4 py-3">
                  <p className="font-semibold text-stone-100">{m.name}</p>
                  <p className="text-xs text-stone-500">{m.email}</p>
                </td>
                <td className="px-4 py-3 text-stone-400">{m.phone || "—"}</td>
                <td className="px-4 py-3 text-stone-400">{m.subject || "—"}</td>
                <td className="max-w-[260px] px-4 py-3 text-sm text-stone-300">{m.message}</td>
                <td className="px-4 py-3 text-xs text-stone-500">
                  {new Date(m.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`admin-delete-query-${m.id}`} onClick={() => remove(m)} className="rounded-lg border border-red-500/40 px-3 py-1.5 text-xs text-red-400 transition-colors hover:bg-red-500/10">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function SubmissionsTable() {
  const [subs, setSubs] = useState(null);

  const load = () =>
    api.get("/admin/submissions").then((r) => setSubs(r.data)).catch(() => toast.error("Could not load submissions"));

  useEffect(() => { load(); }, []);

  const remove = async (s) => {
    if (!window.confirm(`Delete submission "${s.title}" from ${s.name}?`)) return;
    try {
      await api.delete(`/admin/submissions/${s.id}`);
      toast.success("Submission deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!subs) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gold-500" /></div>;
  }

  return (
    <div data-testid="admin-submissions-table" className="overflow-x-auto rounded-xl border border-stone-800">
      <table className="w-full min-w-[820px] text-left text-sm">
        <thead className="bg-ink-900/80 text-[10px] uppercase tracking-[0.2em] text-stone-500">
          <tr>
            <th className="px-4 py-3">From</th>
            <th className="px-4 py-3">Type</th>
            <th className="px-4 py-3">Title</th>
            <th className="px-4 py-3">Pitch</th>
            <th className="px-4 py-3">Links</th>
            <th className="px-4 py-3">Received</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-800/80">
          {subs.length === 0 ? (
            <tr><td colSpan={7} className="px-4 py-10 text-center text-stone-500">No submissions yet.</td></tr>
          ) : (
            subs.map((s) => (
              <tr key={s.id} className="align-top hover:bg-ink-900/50">
                <td className="px-4 py-3">
                  <p className="font-semibold text-stone-100">{s.name}</p>
                  <p className="text-xs text-stone-500">{s.email}</p>
                  {s.phone && <p className="text-xs text-stone-500">{s.phone}</p>}
                </td>
                <td className="px-4 py-3 text-stone-400">{s.content_type || "—"}</td>
                <td className="px-4 py-3 font-medium text-gold-300">{s.title}</td>
                <td className="max-w-[240px] px-4 py-3 text-sm text-stone-300">{s.description || "—"}</td>
                <td className="max-w-[160px] px-4 py-3 text-xs break-all text-stone-500">{s.links || "—"}</td>
                <td className="px-4 py-3 text-xs text-stone-500">
                  {new Date(s.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`admin-delete-submission-${s.id}`} onClick={() => remove(s)} className="rounded-lg border border-red-500/40 px-3 py-1.5 text-xs text-red-400 transition-colors hover:bg-red-500/10">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminPortal({ onClose, onChanged }) {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("shows");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [showForm, setShowForm] = useState(null);
  const [adminMode, setAdminMode] = useState("login");

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setAuthed(false);
      return;
    }
    api.get("/auth/session").then(() => setAuthed(true)).catch(() => {
      localStorage.removeItem(TOKEN_KEY);
      setAuthed(false);
    });
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await api.post("/auth/login", { identifier: email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setAuthed(true);
      toast.success("Welcome back, admin.");
      setEmail("");
      setPassword("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    toast("Signed out of Admin Portal");
  };

  const tabBtn = (active) =>
    `inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] transition-all duration-300 ${
      active ? "bg-gold-500/15 text-gold-300 border border-gold-500/40" : "text-stone-400 border border-transparent hover:text-gold-300"
    }`;

  return (
    <div data-testid="admin-portal-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" data-testid="admin-portal-backdrop" onClick={onClose} />
      <div className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-gold-500/30 bg-[#0c0a07] shadow-[0_0_100px_rgba(212,175,55,0.15)]">
        <div className="flex items-center justify-between border-b border-gold-500/15 px-6 py-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.4em] text-gold-500">VIEWNA Control Room</p>
            <h2 className="mt-1 font-display text-xl font-bold text-stone-50">Admin Portal</h2>
          </div>
          <div className="flex items-center gap-3">
            {authed && (
              <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-stone-700 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-stone-300 transition-colors hover:border-red-500/60 hover:text-red-400">
                <LogOut className="h-3.5 w-3.5" /> Logout
              </button>
            )}
            <button data-testid="admin-portal-close-button" aria-label="Close admin portal" onClick={onClose} className="rounded-full border border-stone-700 p-2.5 text-stone-300 transition-colors hover:border-gold-500/60 hover:text-gold-400">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {authed === null ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-gold-500" /></div>
        ) : !authed ? (
          adminMode === "forgot" ? (
            <div className="mx-auto w-full max-w-sm px-2 py-12">
              <OtpResetForm
                initialIdentifier={email}
                onBack={() => setAdminMode("login")}
                onDone={(msg) => {
                  toast.success(msg || "Password updated successfully! Please Sign In.");
                  setAdminMode("login");
                }}
              />
            </div>
          ) : (
          <form data-testid="admin-login-form" onSubmit={login} className="mx-auto flex w-full max-w-sm flex-col gap-5 px-6 py-14">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold-500/40 bg-gold-500/10 text-gold-400">
              <Lock className="h-5 w-5" />
            </span>
            <p className="text-center font-display text-lg font-bold uppercase tracking-[0.3em] text-gold-300">Admin Sign In</p>
            <input data-testid="admin-email-input" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={INPUT_CLS} />
            <input data-testid="admin-password-input" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={INPUT_CLS} />
            <button data-testid="admin-login-button" type="submit" disabled={loggingIn} className="inline-flex items-center justify-center gap-3 rounded-full bg-gradient-to-r from-gold-600 via-gold-400 to-gold-600 px-8 py-3.5 text-[12px] font-bold uppercase tracking-[0.2em] text-ink-950 transition-all duration-300 hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] disabled:opacity-60">
              {loggingIn && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign In
            </button>
            <button type="button" data-testid="admin-forgot-link" onClick={() => setAdminMode("forgot")} className="text-center text-[11px] font-medium text-gold-400 underline underline-offset-4 transition-colors hover:text-gold-300">
              Forgot Password?
            </button>
          </form>
          )
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 border-b border-stone-800/80 px-6 py-3">
              <button data-testid="admin-tab-shows" onClick={() => { setTab("shows"); setShowForm(null); }} className={tabBtn(tab === "shows")}>
                <LayoutGrid className="h-3.5 w-3.5" /> Shows
              </button>
              <button data-testid="admin-tab-queries" onClick={() => setTab("queries")} className={tabBtn(tab === "queries")}>
                <MessageSquare className="h-3.5 w-3.5" /> Contact Queries
              </button>
              <button data-testid="admin-tab-submissions" onClick={() => setTab("submissions")} className={tabBtn(tab === "submissions")}>
                <Clapperboard className="h-3.5 w-3.5" /> Submissions
              </button>
              {tab === "shows" && (
                <button data-testid="admin-add-show-button" onClick={() => setShowForm("new")} className="ml-auto inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-gold-600 to-gold-400 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-ink-950 transition-shadow hover:shadow-[0_0_20px_rgba(212,175,55,0.35)]">
                  <Plus className="h-3.5 w-3.5" /> Add Show
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              {tab === "shows" ? (
                showForm ? (
                  <AdminShowForm show={showForm === "new" ? null : showForm} onCancel={() => setShowForm(null)} onSaved={() => { setShowForm(null); onChanged(); }} />
                ) : (
                  <ShowsTable onEdit={setShowForm} onChanged={onChanged} />
                )
              ) : tab === "queries" ? (
                <QueriesTable />
              ) : (
                <SubmissionsTable />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
